import { useState } from 'react';
import { useCurrentAccount, useWaysteClient } from '@alliance-disposal/client';
import { Hauler } from '@alliance-disposal/transport-types';
import { useSourContext } from '@wayste/sour-context';
import { Button, Loading, Stepper } from '@wayste/sour-ui';
import { useNavigate } from 'react-router-dom';
import { MaterialForm } from '../../pages/account-settings/components/material-settings';
import routes from '../../utils/routes';
import AccountForm from '../AccountForm';

interface Props {
    onClose: (finished: boolean) => void;
}

const SppNewAccountDialog = ({ onClose }: Props) => {
    const client = useWaysteClient();
    const { hauler } = useCurrentAccount();
    const { setShowAlert } = useSourContext();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [formID, setFormID] = useState('account-settings-form');

    const handleBackClick = () => {
        if (activeStep !== 0) {
            const newStep = activeStep - 1;
            switch (newStep) {
                case 0:
                    setFormID('account-settings-form');
                    break;
                default:
                    setFormID('account-settings-form');
                    break;
            }
            setActiveStep(newStep);
        }
    };

    const handleAccountSubmit = async (data: Partial<Hauler.HaulerTransport>) => {
        setIsLoading(true);
        try {
            await client.hauler().wayste.update({ ...data });

            setFormID('account-material-select-form');
            setActiveStep(1);
        } catch (error) {
            setShowAlert({
                message: 'An error occurred, please try again.',
                severity: 'error',
            });
            setIsLoading(false);
            return;
        } finally {
            setIsLoading(false);
        }
    };

    const handleMaterialSubmit = async (data: any) => {
        setIsLoading(true);
        try {
            if (!hauler) throw new Error('Account not found');
            await client.hauler().wayste.update({ id: hauler.id, materials: data });
            navigate(routes.partnerPortalConnect.list);
            setShowAlert({
                message: 'Lets finish setting up your account from the Partner Connect screen.',
                severity: 'success',
            });
            onClose(true);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            setShowAlert({
                message: 'An error occurred, please try again.',
                severity: 'error',
            });
        }
    };

    const renderView = () => {
        switch (activeStep) {
            case 0:
                return <AccountForm onSubmit={handleAccountSubmit} haulerAccount={hauler} hideSubmit />;
            case 1:
                return (
                    <div>
                        <div>
                            Select the materials that you service. The selected materials will be the only materials available to create
                            pricing and other important info with.
                        </div>
                        {hauler ? <MaterialForm onSubmit={handleMaterialSubmit} account={hauler} /> : <Loading />}
                    </div>
                );
            default:
                return <div>Error missing step</div>;
        }
    };

    return (
        <div className="flex w-full flex-col justify-between gap-2">
            <div className="mx-auto w-full max-w-4xl">
                <div className="mb-6">
                    <Stepper activeStep={activeStep} steps={['Account Info', 'Materials']} />
                </div>
                {renderView()}
            </div>
            <div className="border-t py-4">
                <div className="flex items-end justify-end gap-4">
                    <Button className="btn-secondary-dark-grey" onClick={handleBackClick} disabled={activeStep === 0} loading={isLoading}>
                        Back
                    </Button>
                    <Button type="submit" className="btn-primary" form={formID} loading={isLoading}>
                        Next
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SppNewAccountDialog;
